import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import StoerkIdAPI from '../../../redux/stoerkID/StoerkIdAPI';
import { useAppSelector } from '../../../redux/store.model';
import { getAccountsByWorkspace } from '../../../redux/stoerkID/selectors/StoerkId.selectors';
import { WorkspaceRequestIdParams } from '../util/StoerkId.constants';

export function useCustomerDetailData() {
  const params = useParams<WorkspaceRequestIdParams>();
  const { requestCreatorId, workspaceId: workspaceIdParam, childId } = params;

  const isChildRoute = !!childId;

  // Only fetch admin endpoints when not in a child route (normal users don't have access)
  const {
    error: errorWorkspaces,
    isLoading: isLoadingWorkspaces,
    isError: isErrorWorkspaces,
  } = StoerkIdAPI.useGetAllWorkspaceQuery(undefined, {
    skip: isChildRoute,
  });

  const {
    data: salesTeamRequests,
    error: errorSalesTeamRequests,
    isLoading: isLoadingSalesTeamRequests,
    isError: isErrorSalesTeamRequests,
  } = StoerkIdAPI.useGetSalesTeamRequestsQuery(undefined, {
    skip: isChildRoute,
  });

  const items = useAppSelector(getAccountsByWorkspace);

  const current = useMemo(
    () =>
      items.find(
        (request) =>
          request.lastRequest &&
          request.lastRequest?.creatorId === requestCreatorId
      ) || items.find((request) => request.workspace?.id === workspaceIdParam),
    [items, requestCreatorId, workspaceIdParam]
  );

  const workspaceIdPartial =
    childId ?? workspaceIdParam ?? current?.workspaceId;

  const workspaceFromList = useMemo(
    () =>
      items.find((request) => request.workspace?.id === workspaceIdPartial)
        ?.workspace,
    [items, workspaceIdPartial]
  );

  // Fetch workspace directly by ID if not found in the list (e.g. subworkspaces)
  const { data: workspaceById, isLoading: isLoadingWorkspaceById } =
    StoerkIdAPI.useGetWorkspaceQuery(workspaceIdPartial, {
      skip: !workspaceIdPartial || !!workspaceFromList,
    });

  const workspace = workspaceFromList ?? workspaceById;

  const workspaceId = workspaceIdPartial ?? workspace?.id;

  const {
    error: errorBillingInfo,
    isLoading: isLoadingBillingInfo,
    isError: isErrorBillingInfo,
  } = StoerkIdAPI.useGetBillingInfoFromWorkspaceQuery(workspaceId, {
    skip: !workspaceId,
  });

  const loading =
    isLoadingSalesTeamRequests ||
    isLoadingWorkspaces ||
    isLoadingBillingInfo ||
    isLoadingWorkspaceById;
  const isError =
    isErrorSalesTeamRequests || isErrorWorkspaces || isErrorBillingInfo;
  const error = errorSalesTeamRequests || errorWorkspaces || errorBillingInfo;

  const requestActive = current?.lastRequest;
  const isReady = !!(salesTeamRequests || isChildRoute);

  return {
    workspace,
    workspaceId,
    current,
    requestActive,
    isChildRoute,
    loading,
    isError,
    error,
    isReady,
  };
}
